import { useCallback, useMemo } from 'react';
import { SchemaDescription, SchemaObjectDescription } from 'yup';
import { Alert } from '@mui/material';
import { CardList, ICardProps } from '../components/KPISection/CardList';
import { SubSection } from '../../CompanyProfiles/Summary/components/SubSection';
import { PopupMenu } from '../../../components/PopupMenu/PopupMenu';
import { FormatterService } from '../../../util/formatter-service';
import { FundMetricsKpiData } from '../../../data-models/fund-metrics.data-model';
import { ViewModel } from '../../../models/view.model';
import { fundMetricsByFundFormSchema } from '../../../schemas/FundMetrics.schema';
import { PermissionService } from '../../../services/PermissionService';
import { SelectItem } from '../../../types';
import { IDisplayField } from '../../../view-models/display-field.view-model';
import { useFundSectionComponents } from './useFundSectionComponents';

export enum KPIAggFunc {
  dpi = 'dpi',
  irr = 'irr',
  tvpi = 'tvpi',
  moic = 'moic',
  rvpi = 'rvpi',
  sum = 'sum',
}

export const FUND_KPI_SECTION_ID = 'kpis';

// Ordered: LP TVPI and LP DPI occupy the slots the removed gross-cash-flow totals held.
export const defaultFundKPIs: ReadonlyArray<keyof FundMetricsKpiData> = [
  'contributions',
  'distributions',
  'gpIncentive',
  'lpNav',
  'lpTvpi',
  'lpDpi',
  'netReturn',
];

// Selectable universe: the defaults plus the total-level ratios, mirroring the charts.
const fundKPIUniverse: ReadonlyArray<keyof FundMetricsKpiData> = [...defaultFundKPIs, 'ttvpi', 'dpi', 'rvpi'];

interface IFundKPIsProps {
  kpiData?: FundMetricsKpiData;
  error: boolean;
  loading: boolean;
  view: ViewModel;
}

export function FundsKPIs({ kpiData, error, loading, view }: IFundKPIsProps) {
  const { componentIds, setComponentIds } = useFundSectionComponents(
    view,
    FUND_KPI_SECTION_ID,
    defaultFundKPIs
  );
  const isAuditView = PermissionService.get().isAuditor();

  const fieldDescByName = useMemo(() => {
    const { fields } = fundMetricsByFundFormSchema().fields.metrics.describe() as SchemaObjectDescription;
    return fields as Record<string, SchemaDescription>;
  }, []);

  const visibleKPIs = useMemo(
    () => componentIds.filter((id): id is keyof FundMetricsKpiData => id in fieldDescByName),
    [componentIds, fieldDescByName]
  );

  const kpiCards = useMemo(() => {
    return visibleKPIs.reduce((res, fieldName) => {
      const fieldDesc = fieldDescByName[fieldName];
      const format = FormatterService.get().getFormatterForFormField(
        fieldDesc.meta as IDisplayField<unknown>
      );
      const label = fieldDesc.label!;
      const value = kpiData?.[fieldName];
      const formattedValue = typeof value === 'number' ? format(value) : '-';

      res.push({
        id: fieldName,
        title: label,
        value: formattedValue,
        indicator: null,
      });

      return res;
    }, [] as ICardProps[]);
  }, [fieldDescByName, kpiData, visibleKPIs]);

  const menuItems: SelectItem[] = useMemo(
    () => fundKPIUniverse.map((id) => ({ id, value: fieldDescByName[id]?.label ?? id })),
    [fieldDescByName]
  );

  const selectedItems: SelectItem[] = useMemo(
    () => visibleKPIs.map((id) => ({ id, value: fieldDescByName[id]?.label ?? id })),
    [fieldDescByName, visibleKPIs]
  );

  const handleSelectionChange = useCallback(
    (items: SelectItem[]) => setComponentIds(items.map((item) => String(item.id))),
    [setComponentIds]
  );

  const handleDelete = useCallback(
    (cardId: string) => setComponentIds(visibleKPIs.filter((id) => id !== cardId)),
    [setComponentIds, visibleKPIs]
  );

  // An explicitly emptied KPI list hides the whole panel, header included;
  // restore via the view's Reset action or by promoting/duplicating a default.
  if (componentIds.length === 0) {
    return null;
  }

  if (error) {
    return <Alert severity='error'>Error loading data</Alert>;
  }

  return (
    <SubSection
      title={'Key Performance Indicators'}
      noCard
      actionComponent={
        !isAuditView ? (
          <PopupMenu
            items={menuItems}
            onChange={handleSelectionChange}
            selectedItems={selectedItems}
            label={'Select KPIs'}
            style={{ marginLeft: 'auto' }}
          />
        ) : undefined
      }
    >
      <CardList
        kpis={kpiCards}
        onDelete={handleDelete}
        onReorder={setComponentIds}
        disableDrag={isAuditView}
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(10.5rem, 1fr))',
          opacity: loading ? 0.8 : 1,
        }}
      />
    </SubSection>
  );
}
